// recency — pure wall-clock formatting for the project view's "last edited"
// column (P16) and the activity rail's timestamps.
//
// Given an ISO 8601 instant, a `now` instant (ms since epoch), and a locale,
// it returns a COMPACT relative label ("9h" / "3d" / "12 aug") plus the
// honest ABSOLUTE timestamp for the row's hover/title and screen-reader name.
// Pure: `now` and the locale are passed IN (the impure host reads the clock and
// the active Lingui locale), so this stays testable and the view stays
// Date-free — WorkspaceHome/the rail receive the strings pre-formatted, per
// their contract.
//
// Compact by design (owner-reported: `Intl.RelativeTimeFormat`'s prose form —
// "9 hours ago" / "för 9 timmar sedan" — read as noise in a dense list). Only
// the sub-minute word ("now"/"nu") and, past a week, the month name + digit
// order are localized (via `Intl`); the m/h/d suffixes are short symbolic
// abbreviations, not translated words, so they stay identical across locales
// (the common "9h"/"3d" convention).
//
// Thresholds: <1m → "now"/"nu"; <60m → "Xm"; <24h → "Xh"; <7d → "Xd"; else a
// short absolute date ("12 aug"), with the year appended only when it differs
// from `now`'s year ("12 aug 2024").

export type { Recency };
export { formatRecency };

type Recency = {
    // The compact, human relative label — the visible column.
    readonly relative: string;
    // The full, unambiguous timestamp — the title/hover + the spoken name.
    readonly absolute: string;
};

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;

// The localized "now" word only — every other bucket is a plain digit +
// symbolic suffix, so `Intl.RelativeTimeFormat`'s prose forms never surface.
const nowWord = (locale: string): string =>
    new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(
        0,
        "second",
    );

// The short absolute date fallback past a week — locale-ordered day + month,
// with the year appended only when it isn't the current one (a bare "12 aug"
// already disambiguates within a year).
const shortDate = (then: Date, nowMs: number, locale: string): string =>
    new Intl.DateTimeFormat(locale, {
        day: "numeric",
        month: "short",
        year:
            then.getFullYear() === new Date(nowMs).getFullYear()
                ? undefined
                : "numeric",
    }).format(then);

// The coarsest unit whose magnitude is at least one, floored (not rounded) so
// a 59.6-minute-old edit reads "59m", never a rounded-up "60m". Past a week,
// relative labels stop being legible at a glance — an absolute date takes
// over.
const relativeLabel = (
    then: Date,
    diffMs: number,
    nowMs: number,
    locale: string,
): string => {
    const abs = Math.abs(diffMs);
    if (abs < MINUTE) return nowWord(locale);
    if (abs < HOUR) return `${Math.floor(abs / MINUTE)}m`;
    if (abs < DAY) return `${Math.floor(abs / HOUR)}h`;
    if (abs < WEEK) return `${Math.floor(abs / DAY)}d`;
    return shortDate(then, nowMs, locale);
};

// Format one instant. Returns undefined for an unparseable string so the
// caller renders no column rather than "Invalid Date" (graceful degradation).
const formatRecency = (
    iso: string,
    nowMs: number,
    locale: string,
): Recency | undefined => {
    const then = new Date(iso);
    const thenMs = then.getTime();
    if (Number.isNaN(thenMs)) return undefined;
    return {
        relative: relativeLabel(then, thenMs - nowMs, nowMs, locale),
        absolute: then.toLocaleString(locale, {
            dateStyle: "medium",
            timeStyle: "short",
        }),
    };
};
